import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FirebaseAuth from './FirebaseAuth';
import { X } from 'lucide-react';
import './AuthGuard.css';

const AuthGuard = ({ 
  children, 
  fallback, 
  onUnauthenticated, 
  showSignupModal = true,
  className = '',
  ...props 
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  
  // Close modal when user becomes authenticated
  useEffect(() => {
    if (user && showModal) {
      console.log('User authenticated, closing AuthGuard modal');
      setShowModal(false);
    }
  }, [user, showModal]);

  // Show loading state while checking authentication
  if (loading) {
    return <div className={`auth-guard-loading ${className}`}>{children}</div>;
  }

  // If authenticated, render children normally
  if (isAuthenticated) {
    return <div className={className} {...props}>{children}</div>;
  }

  // Handle unauthenticated click
  const handleUnauthenticatedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onUnauthenticated) {
      onUnauthenticated();
    } else if (showSignupModal) {
      setShowModal(true);
    }
  };

  // Custom fallback component
  if (fallback) {
    return (
      <div className={className} {...props}>
        {fallback}
      </div>
    );
  }

  // Default behavior: wrap children and show signup modal on click
  return (
    <>
      <div 
        className={`auth-guard-wrapper ${className}`} 
        onClick={handleUnauthenticatedClick}
        {...props}
      >
        {React.cloneElement(children, {
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleUnauthenticatedClick(e);
          }
        })}
      </div>
      
      {showSignupModal && showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowModal(false)}
            >
              <X size={24} />
            </button>
            <div className="auth-mode-toggle">
              <button
                className={authMode === 'signin' ? 'active' : ''}
                onClick={() => setAuthMode('signin')}
              >
                Sign In
              </button>
              <button
                className={authMode === 'signup' ? 'active' : ''}
                onClick={() => setAuthMode('signup')}
              >
                Sign Up
              </button>
            </div>
            <FirebaseAuth 
              mode={authMode}
              onSuccess={() => {
                console.log('Authentication successful, waiting for user sync...');
                // Modal will close automatically when user state is set (via useEffect above)
              }}
              onError={(error) => {
                console.error('Authentication error:', error);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};


export default AuthGuard;
